/*
  Cart write spacing guard.

  Problem this solves
  -------------------
  On product pages where the Kaching selector is showing (the Control arm of the
  "[R4][PDP] Kaching Selector Layout" test), one Add to Cart produces two separate
  cart writes: Kaching posts the upsell to /cart/add.js, then the theme's
  product-form.js posts the main product to /cart/add. They land about 250ms apart.

  That spacing is the problem. Shopify is still committing the first write when the
  second arrives, so the second works from a stale cart and the first line is lost --
  the shopper ends up with only the upsell, and because the bundle it discounts
  against never arrived, it is charged at full price. Measured on the live theme:
  simultaneous writes are fine, 500ms+ apart is fine, ~250ms apart loses a line
  almost every time.

  When the custom bundle widget is visible it sends ONE combined /cart/add.js and
  cannot race itself, which is why only the Control arm is affected.

  What this does
  --------------
  Queues cart-mutating POSTs so only one is in flight at a time, and holds each one
  until MIN_GAP has passed since the previous finished. Everything else goes straight
  through untouched. The caller still gets the real Response (or the real rejection),
  so product-form.js, cart.js and cart-drawer.js behave exactly as before -- the
  second write just starts a little later.

  This is deliberately a separate file: global.js is obfuscated and product-form.js is
  minified, so new behaviour belongs beside them, not inside them (AGENTS.md rule 5).

  Remove this once the add path no longer issues two writes in quick succession --
  for example if the Kaching selector test ends with the custom bundle winning, or
  Kaching starts batching the upsell into the same request.
*/
(function () {
  'use strict';

  if (window.extCartWriteGuard) return;
  window.extCartWriteGuard = true;

  // A write must be this far behind the previous one. Measured safe at 500ms on the
  // live theme; 600 leaves margin without being noticeable to a shopper.
  var MIN_GAP = 600;

  var lastFinished = 0;
  var queue = Promise.resolve();

  function noop() {}

  function isCartWrite(url, method) {
    if (String(method || 'GET').toUpperCase() !== 'POST') return false;
    var path = String(url || '').split('?')[0].split('#')[0];
    return /\/cart\/(add|change|update)(\.js)?$/.test(path);
  }

  var nativeFetch = window.fetch;
  if (typeof nativeFetch !== 'function') return;

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var method = (init && init.method) || (input && input.method) || 'GET';

    if (!isCartWrite(url, method)) return nativeFetch.apply(this, arguments);

    var self = this;
    var args = arguments;

    var run = function () {
      var wait = Math.max(0, MIN_GAP - (Date.now() - lastFinished));
      return new Promise(function (resolve) { setTimeout(resolve, wait); })
        .then(function () { return nativeFetch.apply(self, args); })
        .then(
          function (response) { lastFinished = Date.now(); return response; },
          function (error) { lastFinished = Date.now(); throw error; }
        );
    };

    // Run after whatever is already queued, whether it resolved or rejected, so one
    // failed add can never wedge the queue for the rest of the session.
    var result = queue.then(run, run);
    queue = result.then(noop, noop);
    return result;
  };
})();
